(() => {
  'use strict';

  const DAY = 86400000;
  const dateKey = (date) => date.toISOString().slice(0, 10);
  const nextDay = (date) => new Date(date.getTime() + DAY);

  function centralTime(now) {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', hourCycle: 'h23',
    }).formatToParts(now).map(({ type, value }) => [type, value]));
    return { date: new Date(`${parts.year}-${parts.month}-${parts.day}T12:00:00Z`), hour: Number(parts.hour) };
  }

  function holidays(year) {
    const result = new Set();
    function fixed(y, month, day) {
      const date = new Date(Date.UTC(y, month - 1, day, 12));
      result.add(dateKey(date));
      const weekday = date.getUTCDay();
      if (weekday === 6) result.add(dateKey(new Date(date.getTime() - DAY)));
      if (weekday === 0) result.add(dateKey(nextDay(date)));
    }
    function nth(month, weekday, occurrence) {
      const first = new Date(Date.UTC(year, month - 1, 1, 12));
      first.setUTCDate(1 + (weekday - first.getUTCDay() + 7) % 7 + (occurrence - 1) * 7);
      result.add(dateKey(first));
    }
    [[1, 1], [6, 19], [7, 4], [11, 11], [12, 25]].forEach(([m, d]) => fixed(year, m, d));
    fixed(year + 1, 1, 1); // A Saturday New Year is observed on Dec. 31.
    nth(1, 1, 3); nth(2, 1, 3); nth(9, 1, 1); nth(10, 1, 2); nth(11, 4, 4);
    const memorial = new Date(Date.UTC(year, 4, 31, 12));
    memorial.setUTCDate(31 - (memorial.getUTCDay() + 6) % 7);
    result.add(dateKey(memorial));
    return result;
  }

  function businessDay(date, blackoutDates = []) {
    return ![0, 6].includes(date.getUTCDay())
      && !holidays(date.getUTCFullYear()).has(dateKey(date))
      && !blackoutDates.includes(dateKey(date));
  }

  function addBusinessDays(date, days, blackouts) {
    let cursor = new Date(date);
    let remaining = days;
    for (let guard = 0; remaining > 0 && guard < 366; guard += 1) {
      cursor = nextDay(cursor);
      if (businessDay(cursor, blackouts)) remaining -= 1;
    }
    if (remaining) throw new Error('No delivery window within one year');
    return cursor;
  }

  function arrivalWindow(now, { minDays = 3, maxDays = 7, cutoffHour = 15, blackoutDates = [] } = {}) {
    if (!Number.isInteger(minDays) || !Number.isInteger(maxDays) || minDays < 1 || maxDays < minDays || maxDays > 40) return null;
    const local = centralTime(now);
    let dispatch = local.date;
    if (local.hour >= cutoffHour || !businessDay(dispatch, blackoutDates)) dispatch = addBusinessDays(dispatch, 1, blackoutDates);
    return {
      dispatch,
      sameDay: dateKey(dispatch) === dateKey(local.date),
      start: addBusinessDays(dispatch, minDays, blackoutDates),
      end: addBusinessDays(dispatch, maxDays, blackoutDates),
    };
  }

  function stockReady(variant, quantity, preorder, sellingPlan) {
    return !!(variant?.available && variant.physical && variant.tracked
      && Number.isInteger(quantity) && quantity > 0 && quantity <= variant.stock
      && !variant.subscription && !preorder && !sellingPlan);
  }

  const api = { centralTime, holidays, businessDay, arrivalWindow, stockReady };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined' || !window.customElements || customElements.get('flip-delivery-estimate')) return;

  const formatDate = (date) => new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' }).format(date);
  const setText = (node, text) => { if (node.textContent !== text) node.textContent = text; };

  class FlipDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      try {
        this.config = JSON.parse(this.querySelector('[data-delivery-config]').textContent);
        const blackouts = this.config.blackoutDates;
        this.config.blackoutDates = (Array.isArray(blackouts) ? blackouts : String(blackouts).split(','))
          .map((value) => value.trim()).filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value));
      } catch { return; }
      this.form = this.closest('form')
        || this.closest('.product-details')?.querySelector('form[data-product-form]');
      if (!this.form) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.fields = Object.fromEntries(['fallback', 'cost', 'label', 'dates', 'dispatch']
        .map((name) => [name, this.querySelector(`[data-delivery-${name}]`)]));
      this.form.addEventListener('change', (event) => {
        if (this.contains(event.target)) return;
        this.variantOverride = null;
        this.schedule();
      }, options);
      this.form.addEventListener('input', (event) => {
        if (!this.contains(event.target)) this.schedule();
      }, options);
      this.form.addEventListener('variant-change', (event) => {
        this.variantOverride = event.detail?.variant?.id || event.detail?.variant || null;
        this.schedule();
      }, { ...options, capture: true });
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.schedule(); }, options);
      this.observed = new WeakSet();
      this.observer = new MutationObserver((records) => {
        if (records.some(({ target }) => !this.contains(target))) this.schedule();
      });
      this.observer.observe(this.form, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'variant', 'value'] });
      this.resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => this.schedule()) : null;
      // Calendar/cutoff changes while a product tab stays open.
      this.timer = setInterval(() => { if (!document.hidden) this.schedule(); }, 60000);
      this.render();
    }

    disconnectedCallback() {
      this.abort?.abort(); this.observer?.disconnect(); this.resize?.disconnect();
      clearInterval(this.timer); cancelAnimationFrame(this.frame); this.frame = null; this.ready = false;
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; if (this.isConnected) this.render(); });
    }

    nativeDeliveryVisible() {
      const roots = [this.form];
      const candidates = [];
      while (roots.length) {
        const root = roots.pop();
        for (const el of root.querySelectorAll('*')) {
          if (this.contains(el)) continue;
          if (el.shadowRoot) {
            roots.push(el.shadowRoot);
            if (!this.observed.has(el.shadowRoot)) {
              this.observed.add(el.shadowRoot);
              this.observer.observe(el.shadowRoot, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
            }
          }
          if (el.matches('delivery-promise-wc, .delivery-promise__promise-container')) {
            candidates.push(el);
            if (!this.observed.has(el)) { this.observed.add(el); this.resize?.observe(el); }
          }
        }
      }
      return candidates.some((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.width < 1 || rect.height < 1 || getComputedStyle(el).visibility === 'hidden' || getComputedStyle(el).opacity === '0') return false;
        const content = el.shadowRoot || el;
        const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
        let text = ''; let node;
        while ((node = walker.nextNode())) {
          const parent = node.parentElement;
          if (parent && !parent.closest('style,script,svg,[hidden]')
            && getComputedStyle(parent).display !== 'none'
            && getComputedStyle(parent).visibility !== 'hidden') text += ` ${node.textContent}`;
        }
        // The element must contain a real message; an empty reserved slot is not a date.
        return /delivery|arriv|promise/i.test(text);
      });
    }

    render() {
      const native = this.nativeDeliveryVisible();
      this.toggleAttribute('data-native-delivery', native);
      this.fields.fallback.hidden = native;
      if (native) return;
      const selected = this.variantOverride
        || this.form.querySelector('variant-selection')?.getAttribute('variant')
        || this.form.querySelector('[name="id"]')?.value || this.dataset.variant;
      const variant = this.config.variants[String(selected)];
      const quantity = Number(this.form.querySelector('[name="quantity"]')?.value || 1);
      const sellingPlan = this.form.querySelector('[name="selling_plan"]')?.value;
      const isUS = this.dataset.country === 'US';
      setText(this.fields.cost, isUS ? 'Free standard shipping · contiguous U.S.' : 'Shipping information');
      if (!isUS) {
        setText(this.fields.label, 'Delivery information');
        setText(this.fields.dates, 'See shipping options at checkout');
        setText(this.fields.dispatch, 'Our standard arrival estimate applies to the contiguous United States.');
        return;
      }
      if (!stockReady(variant, quantity, this.config.preorder, sellingPlan)) {
        setText(this.fields.label, 'Delivery availability');
        setText(this.fields.dates, !variant ? 'Select an option for delivery information' : !variant.available ? 'Currently unavailable' : 'Check delivery timing before ordering');
        setText(this.fields.dispatch, 'The standard arrival estimate applies to in-stock quantities ready to ship.');
        return;
      }
      const window = arrivalWindow(new Date(), this.config);
      if (!window) return;
      setText(this.fields.label, 'Estimated standard delivery:');
      setText(this.fields.dates, `${formatDate(window.start)} – ${formatDate(window.end)}`);
      setText(this.fields.dispatch, window.sameDay
        ? 'In stock. Order before 3 PM Central for estimated dispatch today.'
        : `In stock. Estimated dispatch ${formatDate(window.dispatch)}.`);
    }
  }
  customElements.define('flip-delivery-estimate', FlipDeliveryEstimate);
})();
